import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, ChevronLeft, Monitor, Smartphone, FileText as PlainIcon,
  CheckCircle2, RefreshCcw, Copy, EyeOff, Eye, Mail, Send, ShieldCheck,
  CalendarClock, XCircle, RotateCcw, AlertTriangle,
} from "lucide-react";
import AdminHeader from "@/components/AdminHeader";
import AdminGate from "@/components/AdminGate";
import {
  getBrief, regenerateBrief, setBriefStatus, getAdminToken,
  approveBrief, scheduleBrief, sendBriefNow, cancelScheduledBrief,
  listBriefDeliveries, retryDelivery, runSchedulerTick,
} from "@/lib/api";
import { toast } from "sonner";
import { formatApiErrorForToast } from "@/lib/apiErrorMessages";
import ConfirmActionDialog from "@/components/action-modals/ConfirmActionDialog";
import TestSendModal from "@/components/action-modals/TestSendModal";

export default function AdminBriefDetail() {
  const [ready, setReady] = useState(!!getAdminToken());
  if (!ready) return <AdminGate onAuthed={() => setReady(true)} />;
  return <AdminBriefDetailInner />;
}

function AdminBriefDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("desktop"); // desktop | mobile | text

  const [confirmModal, setConfirmModal] = useState({ open: false });
  const [testOpen, setTestOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [doc, dels] = await Promise.all([getBrief(id), listBriefDeliveries(id)]);
      setBrief(doc);
      setDeliveries(dels || []);
    } catch (err) {
      toast.error("Couldn't load brief.", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doRegenerate = async () => {
    try {
      const res = await regenerateBrief(id);
      toast.success(`Regenerated as v${res.brief.version}`);
      navigate(`/admin/briefs/${res.brief.id}`);
    } catch (err) {
      toast.error("Regenerate failed.");
    }
  };

  const doStatus = async (status) => {
    try {
      await setBriefStatus(id, status);
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error("Status update failed.");
    }
  };

  const doApprove = async () => {
    try {
      await approveBrief(id);
      toast.success("Approved");
      load();
    } catch (err) {
      toast.error("Approve failed.", { description: formatApiErrorForToast(err) });
    }
  };

  const doSchedule = async () => {
    try {
      const res = await scheduleBrief(id);
      const local = new Date(res.scheduledFor).toLocaleString();
      toast.success("Scheduled", { description: `Send at ${local} (UTC)` });
      load();
    } catch (err) {
      toast.error("Schedule failed.", { description: formatApiErrorForToast(err) });
    }
  };

  const doSendNow = async () => {
    try {
      const res = await sendBriefNow(id);
      if (res.ok) toast.success("Sent to recipient.");
      else toast.warning("Not delivered.", { description: res?.delivery?.failureReason || "See delivery record." });
      load();
    } catch (err) {
      toast.error("Send failed.", { description: formatApiErrorForToast(err) });
    }
  };

  const doCancel = async () => {
    try {
      await cancelScheduledBrief(id);
      toast.success("Scheduled send cancelled");
      load();
    } catch (err) {
      toast.error("Cancel failed.", { description: formatApiErrorForToast(err) });
    }
  };

  const doRetry = async (deliveryId) => {
    try {
      const res = await retryDelivery(deliveryId);
      if (res.ok) toast.success("Retry succeeded");
      else toast.warning("Retry recorded.", { description: res?.delivery?.failureReason || "" });
      load();
    } catch (err) {
      toast.error("Retry failed.", { description: formatApiErrorForToast(err) });
    }
  };

  const runTick = async () => {
    try {
      const res = await runSchedulerTick();
      toast.success(`Tick complete: ${res.attempted} due, ${res.retried} retried, ${res.missed} flagged`);
      load();
    } catch (err) {
      toast.error("Tick failed.");
    }
  };

  const openConfirm = (opts) => setConfirmModal({ open: true, ...opts });

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-brief-detail">
      <AdminHeader onRefresh={load} refreshing={busy} active="briefs" />

      <main className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 py-6 page-fade">
        <button
          onClick={() => navigate("/admin/briefs")}
          data-testid="brief-detail-back"
          className="inline-flex items-center gap-1 text-sm font-medium mb-4"
          style={{ color: "var(--ta-primary)" }}
        >
          <ChevronLeft size={16} /> Back to briefs
        </button>

        {!brief && busy && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {brief && (
          <>
            <BriefHeader brief={brief} onRegenerate={doRegenerate} onStatus={doStatus}
              onApprove={() => openConfirm({
                title: "Approve this brief?",
                description: "Only approved briefs can be scheduled or sent to the customer.",
                confirmLabel: "Approve",
                confirmPhrase: null,
                tone: "primary",
                onConfirm: doApprove,
                testId: "confirm-approve",
              })}
              onSchedule={() => openConfirm({
                title: "Schedule for Monday 7:00 AM local?",
                description: "This will lock a customer delivery at Monday 07:00 in the business's timezone.",
                confirmLabel: "Schedule",
                confirmPhrase: null,
                tone: "primary",
                onConfirm: doSchedule,
                testId: "confirm-schedule",
              })}
              onSendNow={() => openConfirm({
                title: "Send this brief to the real customer now?",
                description: "This will attempt to deliver immediately using the configured provider. Test sends are safer if you just want to preview.",
                confirmLabel: "Send now",
                confirmPhrase: "SEND",
                tone: "warning",
                onConfirm: doSendNow,
                testId: "confirm-send-now",
              })}
              onTestSend={() => setTestOpen(true)}
              onCancelScheduled={() => openConfirm({
                title: "Cancel scheduled delivery?",
                description: "The brief will drop back to 'approved' and no email will go out at the scheduled time.",
                confirmLabel: "Cancel schedule",
                confirmPhrase: null,
                tone: "warning",
                onConfirm: doCancel,
                testId: "confirm-cancel",
              })}
            />

            {/* Preview tabs */}
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4 rounded-full p-1 w-fit" style={{ background: "var(--ta-surface-alt)" }}>
                <TabBtn active={view === "desktop"} icon={Monitor} label="Desktop email" onClick={() => setView("desktop")} testId="view-desktop" />
                <TabBtn active={view === "mobile"} icon={Smartphone} label="Mobile email" onClick={() => setView("mobile")} testId="view-mobile" />
                <TabBtn active={view === "text"} icon={PlainIcon} label="Plain text" onClick={() => setView("text")} testId="view-text" />
              </div>

              <EmailPreview brief={brief} view={view} />
            </div>

            <DeliveryPanel deliveries={deliveries} brief={brief} onRetry={doRetry} onRunTick={runTick} />

            <SelectionSummary brief={brief} />
          </>
        )}
      </main>

      <ConfirmActionDialog
        {...confirmModal}
        onOpenChange={(v) => setConfirmModal((s) => ({ ...s, open: v }))}
      />
      <TestSendModal open={testOpen} onOpenChange={setTestOpen} brief={brief} onSent={load} />
    </div>
  );
}

/* -------- header + meta -------- */

const STATUS_META = {
  draft: { fg: "var(--ta-primary)", bg: "var(--ta-accent)", label: "Draft" },
  ready_for_review: { fg: "#8A5A00", bg: "#FFF3D6", label: "Ready for review" },
  approved: { fg: "var(--ta-success)", bg: "var(--ta-success-bg)", label: "Approved" },
  scheduled: { fg: "var(--ta-secondary)", bg: "#FCE6DE", label: "Scheduled" },
  sent: { fg: "var(--ta-success)", bg: "var(--ta-success-bg)", label: "Sent" },
  failed: { fg: "#B04635", bg: "#FDECEA", label: "Failed" },
};

function BriefHeader({ brief, onRegenerate, onStatus, onApprove, onSchedule, onSendNow, onTestSend, onCancelScheduled }) {
  const meta = STATUS_META[brief.status] || STATUS_META.draft;
  const gen = new Date(brief.generatedAt).toLocaleString();
  const business = brief.business || {};
  const canApprove = brief.status === "draft" || brief.status === "ready_for_review";
  const canSchedule = brief.status === "approved";
  const canSendNow = brief.status === "approved" || brief.status === "scheduled";
  const canCancel = brief.status === "scheduled";
  return (
    <div className="rounded-3xl bg-white border p-6 sm:p-8" style={{ borderColor: "var(--ta-border)" }} data-testid="brief-detail-header">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            v{brief.version}{brief.parentBriefId ? " · regenerated" : ""} · {business.businessName || brief.businessId}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ color: "var(--ta-primary)" }}>
            {brief.subjectLine}
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            Preview text: <span style={{ color: "var(--ta-text)" }}>{brief.previewText}</span>
          </p>
        </div>
        <span
          className="text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full shrink-0"
          style={{ background: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 mb-4">
        <StatBox label="Positive" value={brief.positiveObservationIds?.length ?? 0} />
        <StatBox label="Attention" value={brief.attentionObservationIds?.length ?? 0} />
        <StatBox label="Recommendations" value={brief.recommendationIds?.length ?? 0} />
        <StatBox label="Excluded" value={brief.excludedObservations?.length ?? 0} />
      </div>

      <div className="flex flex-wrap gap-2 pt-3 mt-1 border-t" style={{ borderColor: "var(--ta-border)" }}>
        {brief.status === "draft" && (
          <button
            onClick={() => onStatus("ready_for_review")}
            data-testid="brief-detail-mark-ready"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
          >
            <CheckCircle2 size={16} /> Mark ready for review
          </button>
        )}
        {brief.status === "ready_for_review" && (
          <button
            onClick={() => onStatus("draft")}
            data-testid="brief-detail-return-to-draft"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
          >
            <RefreshCcw size={16} /> Return to draft
          </button>
        )}
        {canApprove && (
          <button
            onClick={onApprove}
            data-testid="brief-detail-approve"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-semibold text-white"
            style={{ background: "var(--ta-primary)" }}
          >
            <ShieldCheck size={16} /> Approve brief
          </button>
        )}
        {canSchedule && (
          <button
            onClick={onSchedule}
            data-testid="brief-detail-schedule"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
            style={{ borderColor: "var(--ta-primary)", color: "var(--ta-primary)", background: "#fff" }}
          >
            <CalendarClock size={16} /> Schedule Monday 7am
          </button>
        )}
        {canSendNow && (
          <button
            onClick={onSendNow}
            data-testid="brief-detail-send-now"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-semibold text-white"
            style={{ background: "var(--ta-secondary)" }}
          >
            <Send size={16} /> Send now
          </button>
        )}
        {canCancel && (
          <button
            onClick={onCancelScheduled}
            data-testid="brief-detail-cancel-scheduled"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
            style={{ borderColor: "#B04635", color: "#B04635", background: "#fff" }}
          >
            <XCircle size={16} /> Cancel scheduled
          </button>
        )}
        <button
          onClick={onTestSend}
          data-testid="brief-detail-test-send"
          className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
          style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
        >
          <Mail size={16} /> Send test email
        </button>
        <button
          onClick={onRegenerate}
          data-testid="brief-detail-regenerate"
          className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
          style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
        >
          <RefreshCcw size={16} /> Regenerate as v{(brief.version ?? 1) + 1}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>Recipient</div>
          <div style={{ color: "var(--ta-primary)" }}>{business.email || "—"}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>Business timezone</div>
          <div style={{ color: "var(--ta-primary)" }}>{business.timezone || "—"}</div>
        </div>
        {brief.scheduledSendAt && (
          <>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>UTC scheduled</div>
              <div style={{ color: "var(--ta-primary)" }}>{new Date(brief.scheduledSendAt).toISOString()}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>Local scheduled</div>
              <div style={{ color: "var(--ta-primary)" }}>
                {new Date(brief.scheduledSendAt).toLocaleString(undefined, { timeZone: business.timezone || "UTC" })}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-4 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        Generated {gen}
      </div>
    </div>
  );
}

const StatBox = ({ label, value }) => (
  <div className="rounded-xl p-3" style={{ background: "var(--ta-surface-alt)" }}>
    <div className="font-display text-2xl font-semibold tabular-nums leading-none" style={{ color: "var(--ta-primary)" }}>{value}</div>
    <div className="text-xs mt-1" style={{ color: "var(--ta-text-secondary)" }}>{label}</div>
  </div>
);

const TabBtn = ({ active, icon: Icon, label, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium transition-colors"
    style={{
      background: active ? "var(--ta-primary)" : "transparent",
      color: active ? "#fff" : "var(--ta-primary)",
    }}
  >
    <Icon size={15} strokeWidth={2.2} /> {label}
  </button>
);

/* -------- Email previews -------- */

function EmailPreview({ brief, view }) {
  if (view === "text") {
    const copy = () => {
      navigator.clipboard.writeText(brief.emailText || "");
      toast.success("Copied plain text");
    };
    return (
      <div
        className="rounded-3xl bg-white border p-5"
        style={{ borderColor: "var(--ta-border)" }}
        data-testid="email-preview-text"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            Plain-text version
          </div>
          <button
            onClick={copy}
            data-testid="copy-plain-text"
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium border"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)" }}
          >
            <Copy size={13} /> Copy
          </button>
        </div>
        <pre
          className="whitespace-pre-wrap font-mono text-sm p-4 rounded-2xl overflow-auto"
          style={{ background: "var(--ta-surface-alt)", color: "var(--ta-text)", maxHeight: 600 }}
        >
          {brief.emailText || "(empty)"}
        </pre>
      </div>
    );
  }

  const width = view === "mobile" ? 390 : 640;
  return (
    <div
      className="rounded-3xl bg-white border p-4 sm:p-6 overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid={`email-preview-${view}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          {view === "mobile" ? "Mobile preview (390px)" : "Desktop preview (640px)"}
        </div>
        <a
          href={`data:text/html,${encodeURIComponent(brief.emailHtml || "")}`}
          target="_blank"
          rel="noreferrer"
          data-testid="open-email-newtab"
          className="text-xs font-medium"
          style={{ color: "var(--ta-primary)" }}
        >
          Open in new tab
        </a>
      </div>
      <div className="w-full overflow-auto rounded-2xl" style={{ background: "#EDEAE4", maxHeight: 720 }}>
        <div className="flex justify-center py-4 px-2">
          <iframe
            title={`email-${view}`}
            srcDoc={brief.emailHtml || "<p>Empty</p>"}
            style={{
              width: `${width}px`,
              maxWidth: "100%",
              height: 720,
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 12,
              background: "#fff",
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* -------- Selection summary (included / excluded) -------- */

function SelectionSummary({ brief }) {
  const positive = brief.positiveObservations || [];
  const attention = brief.attentionObservations || [];
  const excluded = brief.excludedObservations || [];
  const [showExcluded, setShowExcluded] = useState(true);

  return (
    <section className="mt-10" data-testid="selection-summary">
      <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
        Selection engine
      </div>
      <h2 className="font-display text-2xl font-semibold mt-1 mb-4" style={{ color: "var(--ta-primary)" }}>
        What was included &amp; why
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectionColumn
          title="Positive · included"
          count={positive.length}
          tone="positive"
          items={positive.map((o) => ({ id: o.id, title: o.title, meta: `${labelize(o.category)} · ${o.severity}` }))}
          testIdRoot="included-positive"
        />
        <SelectionColumn
          title="Attention · included"
          count={attention.length}
          tone="attention"
          items={attention.map((o) => ({ id: o.id, title: o.title, meta: `${labelize(o.category)} · ${o.severity}` }))}
          testIdRoot="included-attention"
        />
      </div>

      <div className="mt-6 rounded-3xl bg-white border" style={{ borderColor: "var(--ta-border)" }} data-testid="excluded-section">
        <button
          onClick={() => setShowExcluded((v) => !v)}
          data-testid="toggle-excluded"
          className="w-full flex items-center justify-between px-5 py-4"
        >
          <div className="text-left">
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
              Excluded observations
            </div>
            <div className="font-display text-lg font-semibold" style={{ color: "var(--ta-primary)" }}>
              {excluded.length} left out — with reason
            </div>
          </div>
          {showExcluded ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
        {showExcluded && (
          <ul className="border-t px-5 py-4 flex flex-col gap-3" style={{ borderColor: "var(--ta-border)" }}>
            {excluded.length === 0 && (
              <li className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>
                Every observation was included in this brief.
              </li>
            )}
            {excluded.map((e) => (
              <li key={e.observationId} className="text-sm" data-testid={`excluded-item-${e.observationId}`}>
                <div className="font-medium" style={{ color: "var(--ta-primary)" }}>{e.title}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>
                  {labelize(e.category)} · {e.severity} — {e.reason}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SelectionColumn({ title, count, items, tone, testIdRoot }) {
  const bg = tone === "positive" ? "var(--ta-success-bg)" : "var(--ta-accent)";
  const fg = tone === "positive" ? "var(--ta-success)" : "var(--ta-primary)";
  return (
    <div className="rounded-3xl bg-white border p-5" style={{ borderColor: "var(--ta-border)" }} data-testid={testIdRoot}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          {title}
        </div>
        <span
          className="text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full"
          style={{ background: bg, color: fg }}
        >
          {count}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>Nothing selected in this bucket.</div>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((it) => (
            <li key={it.id} data-testid={`${testIdRoot}-item-${it.id}`}>
              <div className="font-medium text-sm" style={{ color: "var(--ta-primary)" }}>{it.title}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>{it.meta}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function labelize(cat) {
  if (!cat) return "";
  return cat.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

/* -------- Delivery panel -------- */

const DELIVERY_STATUS_META = {
  pending:     { fg: "var(--ta-primary)",       bg: "var(--ta-accent)",     label: "Pending" },
  scheduled:   { fg: "var(--ta-secondary)",     bg: "#FCE6DE",              label: "Scheduled" },
  sending:     { fg: "#8A5A00",                 bg: "#FFF3D6",              label: "Sending" },
  sent:        { fg: "var(--ta-success)",       bg: "var(--ta-success-bg)", label: "Sent" },
  delivered:   { fg: "var(--ta-success)",       bg: "var(--ta-success-bg)", label: "Delivered" },
  opened:      { fg: "var(--ta-success)",       bg: "var(--ta-success-bg)", label: "Opened" },
  clicked:     { fg: "var(--ta-success)",       bg: "var(--ta-success-bg)", label: "Clicked" },
  deferred:    { fg: "#8A5A00",                 bg: "#FFF3D6",              label: "Deferred (retrying)" },
  bounced:     { fg: "#B04635",                 bg: "#FDECEA",              label: "Bounced" },
  complained:  { fg: "#B04635",                 bg: "#FDECEA",              label: "Complaint" },
  failed:      { fg: "#B04635",                 bg: "#FDECEA",              label: "Failed" },
  cancelled:   { fg: "var(--ta-text-secondary)",bg: "var(--ta-surface-alt)",label: "Cancelled" },
};

function DeliveryPanel({ deliveries, brief, onRetry, onRunTick }) {
  const customer = deliveries.filter((d) => d.kind === "customer");
  const test = deliveries.filter((d) => d.kind === "test");

  return (
    <section className="mt-10" data-testid="delivery-panel">
      <div className="flex items-end justify-between mb-4 gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            Delivery
          </div>
          <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
            Attempts & provider events
          </h2>
        </div>
        <button
          onClick={onRunTick}
          data-testid="run-scheduler-tick"
          className="inline-flex items-center gap-1 h-10 px-4 rounded-full text-sm font-medium border-2"
          style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
        >
          <RotateCcw size={15} /> Run scheduler tick
        </button>
      </div>

      <DeliveryGroup title="Customer deliveries" items={customer} onRetry={onRetry} testIdRoot="customer-delivery" />
      <DeliveryGroup title="Test sends (audit-only)" items={test} onRetry={onRetry} testIdRoot="test-delivery" />
    </section>
  );
}

function DeliveryGroup({ title, items, onRetry, testIdRoot }) {
  return (
    <div className="mt-4" data-testid={`${testIdRoot}-group`}>
      <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="p-4 rounded-2xl text-sm" style={{ background: "var(--ta-surface-alt)", color: "var(--ta-text-secondary)" }}>
          None yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((d) => (
            <DeliveryRow key={d.id} d={d} onRetry={onRetry} testIdRoot={testIdRoot} />
          ))}
        </div>
      )}
    </div>
  );
}

function DeliveryRow({ d, onRetry, testIdRoot }) {
  const meta = DELIVERY_STATUS_META[d.status] || DELIVERY_STATUS_META.pending;
  const created = new Date(d.createdAt).toLocaleString();
  const canRetry = d.status === "failed" || d.status === "deferred";
  return (
    <div
      className="rounded-2xl bg-white border p-5"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid={`${testIdRoot}-${d.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full"
              style={{ background: meta.bg, color: meta.fg }}
            >
              {meta.label}
            </span>
            <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>attempt {d.attemptNumber || 0}</span>
          </div>
          <div className="text-sm" style={{ color: "var(--ta-primary)" }}>{d.recipientEmail}</div>
          {d.providerMessageId && (
            <div className="text-xs mt-1 font-mono" style={{ color: "var(--ta-text-secondary)" }}>msg id · {d.providerMessageId}</div>
          )}
          {d.failureReason && (
            <div className="mt-2 flex items-start gap-2 p-2 rounded-lg text-xs" style={{ background: "#FDECEA", color: "#B04635" }}>
              <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {d.failureReason}
            </div>
          )}
        </div>
        {canRetry && (
          <button
            onClick={() => onRetry(d.id)}
            data-testid={`${testIdRoot}-retry-${d.id}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-semibold text-white"
            style={{ background: "var(--ta-primary)" }}
          >
            <RotateCcw size={13} /> Retry
          </button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        <div><span className="font-semibold">Created</span> · {created}</div>
        {d.scheduledFor && <div><span className="font-semibold">Scheduled</span> · {new Date(d.scheduledFor).toLocaleString()}</div>}
        {d.retryAt && <div><span className="font-semibold">Retry at</span> · {new Date(d.retryAt).toLocaleString()}</div>}
        {d.deliveredAt && <div><span className="font-semibold">Delivered</span> · {new Date(d.deliveredAt).toLocaleString()}</div>}
        {d.openedAt && <div><span className="font-semibold">Opened</span> · {new Date(d.openedAt).toLocaleString()}</div>}
        {d.firstClickedAt && <div><span className="font-semibold">Clicked</span> · {new Date(d.firstClickedAt).toLocaleString()}</div>}
      </div>

      {d.events?.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs font-medium cursor-pointer" style={{ color: "var(--ta-primary)" }}>
            {d.events.length} event{d.events.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 flex flex-col gap-1 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
            {d.events.map((e, i) => (
              <li key={e.id || i}>
                <span className="font-mono">{new Date(e.at).toLocaleString()}</span> · {e.type}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
